import { useEffect, useState } from "react";
import { MAX_RETRY_DELAY_SECONDS } from "@/features/purchasePoints/config/purchasePoints.config";

const MILLISECONDS_PER_SECOND = 1000;

/**
 * Holds the manual retry back for as long as the provider asked. A single
 * timer, never an automatic replay: the user alone decides to fire the request
 * again.
 *
 * `retryAttempt` tells two consecutive errors carrying the same delay apart:
 * without it the effect dependencies would stay identical, the timer would not
 * be rearmed, and the retry would reopen on the second failure.
 */
export const useRetryDelay = (delaySeconds: number | null, retryAttempt = 0) => {
  const [isDelayElapsed, setIsDelayElapsed] = useState(!delaySeconds);

  useEffect(() => {
    if (!delaySeconds) {
      setIsDelayElapsed(true);
      return undefined;
    }

    setIsDelayElapsed(false);
    // Past its range, `setTimeout` overflows and fires right away: an absurd
    // delay would therefore reopen the retry instead of holding it back.
    const boundedDelay = Math.min(delaySeconds, MAX_RETRY_DELAY_SECONDS);
    const timer = setTimeout(() => setIsDelayElapsed(true), boundedDelay * MILLISECONDS_PER_SECOND);

    return () => clearTimeout(timer);
  }, [delaySeconds, retryAttempt]);

  return isDelayElapsed;
};
